import { memo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Box, Text, ThemeIcon } from '@mantine/core'
import {
  IconWorld,
  IconNetwork,
  IconTargetArrow,
  IconServer,
  IconDatabase,
} from '@tabler/icons-react'

type NodeType = 'domain' | 'alb' | 'target_group' | 'ec2' | 'rds'
type NodeStatus = 'healthy' | 'warning' | 'critical'

interface TopologyNodeData {
  label: string
  type: NodeType
  status: NodeStatus
  [key: string]: unknown
}

const STATUS_COLORS: Record<NodeStatus, string> = {
  healthy: '#2f9e44',
  warning: '#f08c00',
  critical: '#e03131',
}

const STATUS_MANTINE_COLORS: Record<NodeStatus, string> = {
  healthy: 'teal',
  warning: 'yellow',
  critical: 'red',
}

const TYPE_ICONS: Record<NodeType, React.ReactNode> = {
  domain: <IconWorld size={18} />,
  alb: <IconNetwork size={18} />,
  target_group: <IconTargetArrow size={18} />,
  ec2: <IconServer size={18} />,
  rds: <IconDatabase size={18} />,
}

const TYPE_LABELS: Record<NodeType, string> = {
  domain: 'Domain',
  alb: 'ALB',
  target_group: 'Target Group',
  ec2: 'EC2',
  rds: 'RDS',
}

function TopologyNodeComponent({ data, selected }: NodeProps) {
  const nodeData = data as TopologyNodeData
  const { label, type, status } = nodeData
  const borderColor = STATUS_COLORS[status] ?? STATUS_COLORS.healthy
  const iconColor = STATUS_MANTINE_COLORS[status] ?? 'teal'
  const icon = TYPE_ICONS[type] ?? <IconServer size={18} />

  return (
    <>
      <Handle type="target" position={Position.Left} />
      <Box
        style={{
          background: 'var(--mantine-color-dark-6, #1a1b1e)',
          border: `2px solid ${selected ? '#339af0' : borderColor}`,
          borderRadius: 8,
          padding: '6px 10px',
          width: 180,
          boxShadow: selected ? '0 0 0 3px rgba(51, 154, 240, 0.3)' : `0 0 0 1px ${borderColor}22`,
          transition: 'box-shadow 0.15s, border-color 0.15s',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <ThemeIcon size="sm" color={iconColor} variant="light" radius="sm" style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Box style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
          <Text
            size="xs"
            fw={600}
            c="white"
            truncate="end"
            style={{ lineHeight: 1.2 }}
          >
            {label}
          </Text>
          <Text size="10px" c="dimmed">
            {TYPE_LABELS[type] ?? type}
          </Text>
        </Box>
      </Box>
      <Handle type="source" position={Position.Right} />
    </>
  )
}

export const TopologyNode = memo(TopologyNodeComponent)
